import { cdnCommonPath } from "@/constants/cdnUrls";
import { LANGUAGE_NAMES } from "@/constants/languages";
import { PATHS } from "@/constants/pathnames";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { useModal } from "@/contexts/ModalContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLanguageAndCurrency } from "@/hooks/useLanguageAndCurrency";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link, usePathname } from "@/i18n/navigation";
import { getRouteDetails } from "@/utils/routeHelper";
import { getUserMenuData } from "@/utils/userMenuData";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import MobileCurrencySelector from "./MobileCurrencySelector";
import MobileLanguageSelector from "./MobileLanguageSelector";
import NavbarMenuContent from "./NavbarMenuContent";
import TurnaLogo from "./TurnaLogo";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";

const NavBar = memo(function NavBar({
  isOpen,
  isCollapsed,
  close,
  handleMouseEnter,
  handleMouseLeave,
}) {
  const isMobile = useIsMobile();
  const languageModal = useModal("language-modal");
  const currencyModal = useModal("currency-modal");
  const t = useTranslationDetector();
  const locale = useLocale();
  const pathname = usePathname();
  const routeDetails = getRouteDetails(pathname);
  const { isLogin, showLoginModal, getInitials, getUserName, session, logout } =
    useAuth();
  const { showReservationControl } = useHeaderContext();
  const [isOpened, setIsOpened] = useState(false);
  const userMenuData = getUserMenuData(t);
  const { selectedLanguage, selectedCurrency } = useLanguageAndCurrency();
  const { currency } = useCurrency();

  // Animasyon state yönetimi
  const ANIMATION_STATES = useMemo(
    () => ({
      CLOSED: "closed",
      OPENING: "opening",
      OPEN: "open",
      CLOSING: "closing",
    }),
    [],
  );

  const [animationState, setAnimationState] = useState(ANIMATION_STATES.CLOSED);

  // isOpen prop'una göre animasyon state'ini yönet
  useEffect(() => {
    if (isOpen && isMobile) {
      setAnimationState(ANIMATION_STATES.OPENING);
    } else if (!isOpen && isMobile) {
      setAnimationState(ANIMATION_STATES.CLOSING);
    }
  }, [isOpen, isMobile, ANIMATION_STATES]);

  // Animasyon state geçişlerini yönet
  useEffect(() => {
    if (animationState === ANIMATION_STATES.OPENING) {
      const timer = setTimeout(() => {
        setAnimationState(ANIMATION_STATES.OPEN);
      }, 10);
      return () => clearTimeout(timer);
    } else if (animationState === ANIMATION_STATES.CLOSING) {
      const timer = setTimeout(() => {
        setAnimationState(ANIMATION_STATES.CLOSED);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [animationState, ANIMATION_STATES]);

  // Portal render kontrolü
  const shouldRenderPortal =
    isMobile &&
    (animationState === ANIMATION_STATES.OPENING ||
      animationState === ANIMATION_STATES.OPEN ||
      animationState === ANIMATION_STATES.CLOSING);

  const isAnimating = isMobile && animationState === ANIMATION_STATES.OPEN;

  // Portal close handler
  const handlePortalClose = useCallback(() => {
    setAnimationState(ANIMATION_STATES.CLOSING);
    close();
  }, [ANIMATION_STATES, close]);

  const menuData = [
    {
      id: "flight",
      text: t("navbar.flights"),
      icon: byPrefixAndName.fak["new-flight"],
      href: PATHS.FLIGHT_TICKET.PATH,
    },
    {
      id: "hotel",
      text: t("navbar.hotel"),
      icon: byPrefixAndName.fak["new-hotel"],
      href: PATHS.HOTEL.PATH,
    },
    {
      id: "bus",
      text: t("navbar.bus"),
      icon: byPrefixAndName.fak["new-bus"],
      href: PATHS.BUS_TICKET.PATH,
    },
    {
      id: "car",
      text: t("navbar.car"),
      icon: byPrefixAndName.fak["new-car"],
      href: PATHS.CAR_RENTAL.PATH,
    },
    {
      id: "ferry",
      text: t("navbar.ferry"),
      icon: byPrefixAndName.fak["new-ferry"],
      href: PATHS.FERRY_TICKET.PATH,
    },
  ];

  const overlayClasses = useMemo(
    () =>
      clsx(
        "bg-foreground/60 absolute top-full right-0 transition-all duration-500 ease-in-out",
        {
          // Overlay sadece menu açık olduğunda görünür
          "h-screen w-full cursor-pointer opacity-100": isOpen,
          "pointer-events-none opacity-0": !isOpen,
        },
      ),
    [isOpen],
  );

  const navClasses = useMemo(() => {
    return clsx(
      "group border-primary-weak absolute top-full hidden h-screen overflow-hidden border-t bg-white px-3 py-6 transition-all duration-500 ease-in-out md:block",
      locale === "ar" ? "right-0 border-l" : "left-0 border-r",
      !isOpen && isCollapsed && "md:hover:w-61",
      isOpen && !isCollapsed && "translate-x-0",
      !isOpen &&
        !isCollapsed &&
        (locale === "ar" ? "translate-x-full" : "-translate-x-full"),
      isCollapsed && !isOpen && "w-18",
      isOpen && "w-61",
    );
  }, [isOpen, isCollapsed, locale]);

  const openLanguageModal = () => {
    languageModal.open({
      isBottomSheet: true,
      contentClassName: "!p-0",
      title: t("common.languageLabel"),
    });
  };

  const openCurrencyModal = () => {
    currencyModal.open({
      isBottomSheet: true,
      contentClassName: "!p-0",
      title: t("common.currencyLabel"),
    });
  };

  return !isMobile ? (
    <>
      <button className={overlayClasses} onClick={close}></button>
      <nav
        className={navClasses}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <NavbarMenuContent isCollapsed={isCollapsed} />
      </nav>
    </>
  ) : (
    <>
      {/* Mobile portal - tam ekran slide up modal */}
      {shouldRenderPortal &&
        isMobile &&
        createPortal(
          <div
            className={clsx(
              "fixed inset-0 z-50 flex h-full flex-col bg-white",
              "transform transition-all duration-300",
              isAnimating
                ? "translate-y-0 ease-out"
                : "translate-y-full ease-in",
            )}
          >
            {/* Header */}
            <div className="menu-header border-primary-weak flex shrink-0 items-center justify-between border-b bg-white px-4 py-3">
              <TurnaLogo className="text-primary w-24 md:w-34" />
              <button onClick={handlePortalClose}>
                <FontAwesomeIcon
                  icon={byPrefixAndName.fak["x-mark-custom"]}
                  className="text-foreground text-[24px]"
                />
              </button>
            </div>
            {/* Content */}
            <div className="bg-background-global flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
              <div>
                {isLogin ? (
                  <button
                    className="shadow-small flex w-full flex-col gap-4 rounded-lg bg-white p-4"
                    onClick={() => setIsOpened(!isOpened)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {isOpened && (
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fal["angle-left"]}
                            className="text-foreground-subtle"
                          />
                        )}
                        <div className="bg-primary flex h-10 w-10 items-center justify-center rounded-full font-medium text-white">
                          {getInitials()}
                        </div>
                        <div className="flex flex-col text-left">
                          <div className="text-primary text-lg font-medium">
                            {getUserName()}
                          </div>
                          <span className="text-sm font-medium">
                            {session?.Email || ""}
                          </span>
                        </div>
                      </div>

                      {!isOpened && (
                        <FontAwesomeIcon
                          icon={byPrefixAndName.fal["angle-right"]}
                          className="text-foreground-subtle"
                        />
                      )}
                    </div>
                    {isOpened && (
                      <>
                        <div className="border-border-weaker border-b"></div>
                        <div className="text-primary flex items-center justify-start gap-3 text-sm leading-0">
                          <Image
                            src={`${cdnCommonPath}/wallet-two-tone.svg`}
                            alt={session?.Name}
                            width={24}
                            height={24}
                          />
                          <span className="font-semibold">
                            {session?.UserPointTotal?.TotalPoint}
                          </span>
                          <span>{t("common.turnaPoint")}</span>
                        </div>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="flex flex-col gap-4">
                    <span className="text-lg font-medium">
                      {t("loginmodal.title")}
                    </span>
                    <div className="flex items-center justify-between gap-4">
                      <Button
                        onClick={() => showLoginModal(false)}
                        className="w-full"
                      >
                        {t("common.login")}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => showLoginModal(true)}
                        className="w-full"
                      >
                        {t("registermodal.register")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {isOpened ? (
                <div className="flex flex-col gap-4">
                  <div className="shadow-small flex flex-col gap-2 rounded-lg bg-white p-4">
                    {userMenuData.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <Link
                          href={item.href}
                          className="flex items-center justify-between py-3"
                        >
                          <span className="flex items-center gap-3">
                            <span className="text-primary">{item.icon}</span>
                            <span className="text-foreground font-medium">
                              {item.text}
                            </span>
                          </span>
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fal["angle-right"]}
                            className="text-foreground-subtle"
                          />
                        </Link>
                        {index < userMenuData.length - 1 && (
                          <div className="border-border-weaker border-b"></div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <button
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-4 py-3 transition-colors"
                    onClick={logout}
                  >
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fak["log-out"]}
                      className="text-primary text-[22px]"
                    />
                    <span className="font-medium">{t("usermenu.logout")}</span>
                  </button>
                </div>
              ) : (
                <>
                  {isLogin && (
                    <div className="shadow-small flex flex-col gap-2 rounded-lg bg-white p-4">
                      <Link
                        href={PATHS.TRAVELS_PAGE.PATH}
                        className="flex items-center justify-between py-3"
                      >
                        <span className="flex items-center gap-3">
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["luggage"]}
                            className="text-primary text-[22px]"
                          />
                          <span className="text-foreground font-medium">
                            {t("usermenu.mytrips")}
                          </span>
                        </span>
                        <FontAwesomeIcon
                          icon={byPrefixAndName.fal["angle-right"]}
                          className="text-foreground-subtle"
                        />
                      </Link>
                      <div className="border-border-weaker border-b"></div>
                      {/* <button
                        className="flex items-center justify-between py-3"
                        onClick={showReservationControl}
                      >
                        <span className="flex items-center gap-3">
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["message-chat"]}
                            className="text-primary text-[22px]"
                          />
                          <span className="text-foreground font-medium">
                            {t("common.reservationcontrol")}
                          </span>
                        </span>
                        <FontAwesomeIcon
                          icon={byPrefixAndName.fal["angle-right"]}
                          className="text-foreground-subtle"
                        />
                      </button> */}
                      <Link
                        href={PATHS.POINT_DETAILS_PAGE.PATH}
                        className="flex items-center justify-between py-3"
                      >
                        <span className="flex items-center gap-3">
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["credit-card-coins"]}
                            className="text-primary text-[22px]"
                          />
                          <span className="text-foreground font-medium">
                            {t("usermenu.pointdetails")}
                          </span>
                        </span>
                        <FontAwesomeIcon
                          icon={byPrefixAndName.fal["angle-right"]}
                          className="text-foreground-subtle"
                        />
                      </Link>
                    </div>
                  )}

                  <div className="shadow-small flex flex-col gap-2 rounded-lg bg-white p-4">
                    {menuData.map((item, index) => {
                      const isActive =
                        (item.href === "/flight" &&
                          routeDetails.isFlightHome) ||
                        (item.href === "/hotel" && routeDetails.isHotelHome) ||
                        (item.href === "/car-rental" &&
                          routeDetails.isCarRentalHome) ||
                        (item.href === "/ferry" &&
                          routeDetails.isFerryTicketHome) ||
                        (item.href === "/bus-ticket" &&
                          routeDetails.isBusTicketHome);

                      return (
                        <React.Fragment key={item.id}>
                          {isActive ? (
                            <button
                              onClick={handlePortalClose}
                              className="flex items-center justify-between py-3"
                            >
                              <span className="flex items-center gap-3">
                                <FontAwesomeIcon
                                  icon={item.icon}
                                  className="text-primary text-[22px]"
                                />
                                <span className="text-foreground font-medium">
                                  {item.text}
                                </span>
                              </span>
                              <FontAwesomeIcon
                                icon={byPrefixAndName.fal["angle-right"]}
                                className="text-foreground-subtle"
                              />
                            </button>
                          ) : (
                            <Link
                              href={item.href}
                              className="flex items-center justify-between py-3"
                            >
                              <span className="flex items-center gap-3">
                                <FontAwesomeIcon
                                  icon={item.icon}
                                  className="text-primary text-[22px]"
                                />
                                <span className="text-foreground font-medium">
                                  {item.text}
                                </span>
                              </span>
                              <FontAwesomeIcon
                                icon={byPrefixAndName.fal["angle-right"]}
                                className="text-foreground-subtle"
                              />
                            </Link>
                          )}
                          {index < menuData.length - 1 && (
                            <div className="border-border-weaker border-b"></div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  <div className="shadow-small flex flex-col gap-2 rounded-lg bg-white p-4">
                    <Link
                      href={PATHS.CONTACT.PATH}
                      className="flex items-center justify-between py-3"
                    >
                      <span className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={byPrefixAndName.fak["question-circle"]}
                          className="text-primary text-[22px]"
                        />
                        <span className="text-foreground font-medium">
                          {t("common.help")}
                        </span>
                      </span>
                      <FontAwesomeIcon
                        icon={byPrefixAndName.fal["angle-right"]}
                        className="text-foreground-subtle"
                      />
                    </Link>
                    <div className="border-border-weaker border-b"></div>
                    <Link
                      href={PATHS.FAQ_PAGE.PATH}
                      className="flex items-center justify-between py-3"
                    >
                      <span className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={byPrefixAndName.fak["message-chat"]}
                          className="text-primary text-[22px]"
                        />
                        <span className="text-foreground font-medium">
                          {t("footer.flightmenu5link2")}
                        </span>
                      </span>
                      <FontAwesomeIcon
                        icon={byPrefixAndName.fal["angle-right"]}
                        className="text-foreground-subtle"
                      />
                    </Link>
                    <div className="border-border-weaker border-b"></div>
                    <Link
                      href={PATHS.ABOUT_US.PATH}
                      className="flex items-center justify-between py-3"
                    >
                      <span className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={byPrefixAndName.fak["building-modern-3-tree"]}
                          className="text-primary text-[22px]"
                        />
                        <span className="text-foreground font-medium">
                          {t("footer.flightmenu4link1")}
                        </span>
                      </span>
                      <FontAwesomeIcon
                        icon={byPrefixAndName.fal["angle-right"]}
                        className="text-foreground-subtle"
                      />
                    </Link>
                  </div>

                  <div className="flex flex-col gap-4">
                    <span className="text-lg font-medium">
                      {t("common.preferences")}
                    </span>
                    <div className="shadow-small flex flex-col gap-2 rounded-lg bg-white p-4">
                      <button
                        className="flex items-center justify-between py-3"
                        onClick={openLanguageModal}
                      >
                        <span className="flex items-center gap-3">
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["globe"]}
                            className="text-primary text-[22px]"
                          />
                          <span className="text-foreground font-medium">
                            {t("common.languageLabel")}
                          </span>
                        </span>
                        <div className="inline-flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-2">
                            <Image
                              src={`${cdnCommonPath}${selectedLanguage}.svg`}
                              alt={selectedLanguage}
                              width={24}
                              height={24}
                            />
                            <span className="font-medium">
                              {LANGUAGE_NAMES[selectedLanguage].name}
                            </span>
                          </span>
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fal["angle-right"]}
                            className="text-foreground-subtle"
                          />
                        </div>
                      </button>
                      <div className="border-border-weaker border-b"></div>

                      <button
                        className="flex items-center justify-between py-3"
                        onClick={openCurrencyModal}
                      >
                        <span className="flex items-center gap-3">
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["banknote"]}
                            className="text-primary text-[22px]"
                          />
                          <span className="text-foreground font-medium">
                            {t("common.currencyLabel")}
                          </span>
                        </span>
                        <div className="inline-flex items-center justify-between gap-3">
                          <span className="font-medium">{currency}</span>

                          <FontAwesomeIcon
                            icon={byPrefixAndName.fal["angle-right"]}
                            className="text-foreground-subtle"
                          />
                        </div>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
      <Modal id="language-modal">
        <MobileLanguageSelector onChangeComplete={languageModal.close} />
      </Modal>
      <Modal id="currency-modal">
        <MobileCurrencySelector onChangeComplete={currencyModal.close} />
      </Modal>
    </>
  );
});

export default NavBar;
